// Single source of truth for the paywall nav-config flag, its sticky (per-tab)
// resolution and the auth-arm label. Read by GrowthBookContext (drives the
// funnel) and everflow (reports it as sub7) — if the two diverge, Everflow
// reports an arm the user was never in.
//
// Kept dependency-free: utils/paywall imports utils/everflow, so any import
// added here risks a cycle.

export const PAYWALL_NAV_CONFIG_FLAG = 'createaitrans-paywall-nav-config';
export const DEFAULT_NAV_CONFIG = { type: 'paywall' };

// Nav-config types that need sticky (per-tab) resolution because their GB rules
// are URL-targeted and the quiz SPA navigation strips the query params.
export const STICKY_NAV_TYPES = ['paywall_popup', 'simple_registration'];

const PERSIST_KEY = 'cait_paywall_nav_config';

// These arms have URL-targeted GB rules, but SPA navigation strips the query
// params, so GB reverts to the default mid-funnel. Remember the first non-default
// resolution and fall back to it. sessionStorage (not localStorage) so it is
// scoped to this tab/visit and never pins a user across sessions.
export const resolveNavConfig = (liveConfig) => {
  if (typeof window === 'undefined') return liveConfig || DEFAULT_NAV_CONFIG;

  if (STICKY_NAV_TYPES.includes(liveConfig?.type)) {
    try {
      sessionStorage.setItem(PERSIST_KEY, JSON.stringify(liveConfig));
    } catch (e) {
      // ignore storage failures
    }
    return liveConfig;
  }

  // Live value reverted to default — reuse a sticky config remembered this visit.
  try {
    const raw = sessionStorage.getItem(PERSIST_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (STICKY_NAV_TYPES.includes(parsed?.type)) return parsed;
  } catch (e) {
    // ignore
  }

  return liveConfig || DEFAULT_NAV_CONFIG;
};

// Reserved for "GrowthBook never resolved" (see everflow's
// resolveAuthTypeForClick) — NOT for an arm we don't recognise.
export const AUTH_TYPE_UNKNOWN = 'unknown';

// The arm reported as Everflow sub7 and on every GTM event.
//
// `type` is passed through VERBATIM. The flag's value space is open and grows
// without code: paywall, chat_paywall, chat, paywall_popup, paywall_popup_first,
// simple_registration, duel, make-her-move … — a closed whitelist would turn every
// arm added later into a single `unknown` bucket and need an edit per arm.
export const resolveAuthType = (config) => {
  // A missing type is the default arm, matching how the funnel reads it.
  const type = (config?.type || DEFAULT_NAV_CONFIG.type).toString().trim();
  // `paywall_popup_first` can also arrive as an ORDERING alongside the type: that
  // arm keeps `type: 'paywall_popup'` so backend/platform checks on that literal
  // stay true, and rides the variant in `order`. Both spellings land on the same
  // label. (utils/paywall's popupTypeLabel produces it too — collapse at merge.)
  if (config?.order === 'popup_first' && !type.endsWith('_first')) {
    return `${type}_first`;
  }
  return type;
};
